import { useOrderConfirmationQuery } from '@api/api';
import { TextCodes } from '@api/types/translation/TextCodes';
import { BlockContainer, Html, LoadingSkeleton, OrderSummary, Title } from '@components';
import { Box, Grid, styled } from '@mui/material';
import { t } from '@translations';
import { replaceTemplateSyntax } from '@utils/text.utils';
import { useLocation } from 'react-router-dom';

const ConfirmationBlockWrapper = styled(Box)``;

type OrderConfirmationProps = {
  title?: string;
  messageForOk?: string;
  messageForNok?: string;
  messageForError?: string;
  messageForDraft?: string;
  children: React.ReactNode;
};

export const ConfirmationBlock = ({
  title,
  children,
  messageForDraft,
  messageForOk,
  messageForError,
}: OrderConfirmationProps) => {
  const location = useLocation();
  const lastPartOfUrl = location.pathname.split('/').pop();
  const { isError, isFetching, isLoading, currentData } = useOrderConfirmationQuery(
    lastPartOfUrl ?? ''
  );
  const isWorking = isFetching || isLoading;
  const orderType = currentData?.data?.orderType; //"NormalOrder" | "DraftOrder"
  const message = selectMessage(isError, orderType, messageForOk, messageForDraft, messageForError);
  const templates = {
    OrderNo: lastPartOfUrl ?? '',
    OrderEmail: currentData?.data?.customerEmail ?? '',
  };
  return (
    <BlockContainer>
      <ConfirmationBlockWrapper className='confirmation-block'>
        <Title>{title || t(TextCodes.Product.Checkout.OrderConfirmation)}</Title>
        <Box sx={{ mt: 8 }}>
          <Grid container spacing={{ xs: 2, md: 4 }}>
            <Grid item xs={12} md={8}>
              {isWorking ? (
                <>
                  <LoadingSkeleton variant='text' width='80%' height={20} sx={{ my: 2 }} />
                  <LoadingSkeleton variant='text' width='70%' height={20} sx={{ my: 2 }} />
                  <LoadingSkeleton variant='text' width='60%' height={20} sx={{ my: 2 }} />
                </>
              ) : (
                <Html html={replaceTemplateSyntax(message, templates)} />
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              {isWorking ? (
                <LoadingSkeleton variant='rectangular' width='100%' height={340} />
              ) : (
                <OrderSummary
                  title={t(TextCodes.Product.Checkout.OrderSummary)}
                  itemCount={currentData?.data?.productsCount}
                  subtotal={currentData?.data?.subTotal}
                  tax={currentData?.data?.taxTotal}
                  total={currentData?.data?.total}
                />
              )}
            </Grid>
          </Grid>
        </Box>
      </ConfirmationBlockWrapper>

      {children}
    </BlockContainer>
  );
};

const selectMessage = (
  isError?: boolean,
  orderType?: string,
  messageForOk?: string,
  messageForDraft?: string,
  messageForError?: string
) => {
  if (isError) {
    return messageForError;
  } else if (orderType === 'DraftOrder') {
    return messageForDraft;
  } else {
    return messageForOk;
  }
};
